import dayjs from 'dayjs'
import { RepaymentMethods } from 'share/enums/payment'

class LoanCalculator {
  #amount: number
  #interestRate: number
  #period: number
  #repaymentMethod: RepaymentMethods

  constructor(amount: number, period: number, interestRate: number, repaymentMethod: RepaymentMethods) {
    this.#amount = amount
    this.#interestRate = interestRate
    this.#period = period
    this.#repaymentMethod = repaymentMethod
  }

  public getCalcResults() {
    return {
      gesv: this.getGesv(),
      monthlyPayment: this.getMonthlyPayment(),
      overpayment: this.getOverpayment()
    }
  }

  private getGesv() {
    const annuityGesv = this.calculateAnnuityGesv()
    if (!annuityGesv) return 0
    if (this.#repaymentMethod === RepaymentMethods.ANNUITY) return annuityGesv
    if (this.#repaymentMethod === RepaymentMethods.EQUAL_INSTALMENTS) return (annuityGesv + 0.1).toFixed(1)
  }

  private getMonthlyPayment() {
    if (this.#repaymentMethod === RepaymentMethods.ANNUITY) return this.calculatePaymentAnnuity()
    if (this.#repaymentMethod === RepaymentMethods.EQUAL_INSTALMENTS) return this.calculatePaymentEqualInstallments()
    return 0
  }

  private getOverpayment() {
    if (this.#repaymentMethod === RepaymentMethods.ANNUITY) {
      const payment = this.calculatePaymentAnnuity()
      return this.calculateOverpaymentAnnuity(payment)
    }

    if (this.#repaymentMethod === RepaymentMethods.EQUAL_INSTALMENTS) {
      return this.calculateOverpaymentEqualInstallments()
    }
    return 0
  }

  private calculatePaymentEqualInstallments() {
    const monthlyInterest = this.#interestRate / 12 / 100
    const mainDebtPayment = this.#amount / this.#period
    const interest_payment = this.#amount * monthlyInterest

    return +(interest_payment + mainDebtPayment).toFixed(2)
  }

  private calculatePaymentAnnuity(): number {
    const coef = this.getTotalCoefficent()
    return Math.round(this.#amount / coef)
  }

  private calculateOverpaymentEqualInstallments() {
    const monthlyRate = this.#interestRate / 12 / 100
    const totalPayments =
      (this.#amount / this.#period) * this.#period + (this.#amount * monthlyRate * (this.#period + 1)) / 2

    const overpayment = totalPayments - this.#amount
    return overpayment
  }

  private calculateOverpaymentAnnuity(payment: number) {
    const totalPayments = payment * this.#period
    const overpayment = totalPayments - this.#amount
    return overpayment
  }

  private getTotalCoefficent() {
    let currentDate = dayjs()
    let totalDays = 0
    let totalCoef = 0

    for (let i = 0; i < this.#period; i++) {
      const dayCount = 30
      totalDays += dayCount
      const coef = 1 / (1 + this.#interestRate / 100 / 12) ** (totalDays / 30)
      totalCoef += coef
      currentDate = currentDate.add(dayCount, 'day')
    }

    return totalCoef
  }

  private calculateAnnuityGesv() {
    switch (true) {
      case this.#period <= 6:
        return 26.1
      case this.#period > 6 && this.#period <= 12:
        return 24.2
      case this.#period > 12 && this.#period <= 24:
        return 23.1
      case this.#period > 24 && this.#period <= 36:
        return 22.8
      case this.#period > 36 && this.#period <= 48:
        return 22.6
      case this.#period > 48 && this.#period <= 60:
        return 22.5
    }
  }
}

export default LoanCalculator
